import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Search, Filter, Download, Upload, Save, Loader2, Settings,
  AlertCircle, Check, X, Mail, Phone, Globe, MapPin, User,
  Star, TrendingUp, Package, FileText, ExternalLink,
  Plus, Edit, Trash2, MessageSquare, Calendar, Clock,
  Moon, Sun, Sparkles, Building, Users, Briefcase,
  ChevronDown, Link as LinkIcon, Share2
} from 'lucide-react';
import { websearchService } from '../../services/websearchService';
import { functions } from '../../config/firebase';
import { httpsCallable } from 'firebase/functions';
import { AddLeadModal } from '../websearch/AddLeadModal';
import EngagementSidebar from '../websearch/EngagementSidebar';
import UserProfile from '../shared/SharedUserProfile';
import { AuthModal } from '../auth/AuthModal';  
import { ShareModal } from '../books/modals/ShareModal';
import { ProfileModal } from '../shared/ProfileModal';
import { WebSearchManager } from '../websearch/WebSearchManager';

const COUNTRIES = [
  'United States', 'United Kingdom', 'Canada', 'Australia', 'South Africa',
  'India', 'Germany', 'France', 'Spain', 'Italy', 'Netherlands', 'Belgium',
  'Switzerland', 'Austria', 'Poland', 'Sweden', 'Norway', 'Denmark', 'Finland',
  'Ireland', 'New Zealand', 'Singapore', 'Hong Kong', 'Japan', 'China',
  'Brazil', 'Mexico', 'Argentina', 'Chile', 'Colombia', 'United Arab Emirates',
  'Saudi Arabia', 'Israel', 'Turkey', 'Egypt', 'Nigeria', 'Kenya', 'Ghana'
];

// Helper to check if two items are effectively the same
const isSameItem = (itemA, itemB) => {
  const norm = (str) => (str || '').toLowerCase().trim();
  
  if (itemA.type !== itemB.type) return false;

  if (itemA.type === 'lead' || itemA.type === 'supplier') {
    const a = itemA;
    const b = itemB;
    
    if (a.email && b.email && norm(a.email) === norm(b.email)) return true;
    if (a.linkedin && b.linkedin && norm(a.linkedin) === norm(b.linkedin)) return true;
    
    if (a.type === 'lead' && b.type === 'lead') {
      if (norm(a.name) === norm(b.name) && norm(a.company) === norm(b.company)) return true;
    } else {
      if (norm(a.name) === norm(b.name)) return true;
    }
  }
  
  if (itemA.type === 'tender' && itemB.type === 'tender') {
    if (norm(itemA.title) === norm(itemB.title) && norm(itemA.organization) === norm(itemB.organization)) return true;
    if (itemA.link && itemB.link && norm(itemA.link) === norm(itemB.link)) return true;
  }

  return false;
};

export const WebSearchTemplate = ({ bookId, bookData, user, logout, viewManager, customStyling, enableShareTracking = false }) => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [groundingUrls, setGroundingUrls] = useState(bookData?.websearchSettings?.groundingUrls || []);
  const [isSearching, setIsSearching] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [category, setCategory] = useState(bookData?.websearchSettings?.defaultCategory || 'lead');
  const [savedItems, setSavedItems] = useState([]);
  const [viewModeState, setViewModeState] = useState('search');
  const { viewMode, isAuthenticated, hasManagementAccess } = viewManager || { viewMode: 'public', isAuthenticated: false, hasManagementAccess: false };
  const [isDarkMode, setIsDarkMode] = useState(true);
  const [isAddLeadModalOpen, setIsAddLeadModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [sidebarItem, setSidebarItem] = useState(null);
  
  const [locationFilter, setLocationFilter] = useState({
    country: bookData?.websearchSettings?.defaultLocation?.country || '',
    region: bookData?.websearchSettings?.defaultLocation?.region || '',
    city: bookData?.websearchSettings?.defaultLocation?.city || ''
  });
  const [showTargetUrl, setShowTargetUrl] = useState(false);
  const [targetUrls, setTargetUrls] = useState(['']);

  const [showShareModal, setShowShareModal] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [isOwner, setIsOwner] = useState(false);
  const [showUserProfile, setShowUserProfile] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);

  const [selectedPromptId, setSelectedPromptId] = useState(null);

  const [searchPrompts, setSearchPrompts] = useState([]);
  const [selectedPrompt, setSelectedPrompt] = useState(null);
  const [editablePrompt, setEditablePrompt] = useState('');
  const [showPromptEditor, setShowPromptEditor] = useState(false);

  const [showApiKeyPrompt, setShowApiKeyPrompt] = useState(false);

  const headerTitle = bookData?.websearchSettings?.headerTitle || 'Lead Scout';
  const headerSubtitle = bookData?.websearchSettings?.headerSubtitle || 'Find Your Next Opportunity';
  const enabledCategories = bookData?.websearchSettings?.enabledCategories || ['lead', 'supplier', 'tender'];

  useEffect(() => {
    if (user && bookData) {
      setIsOwner(user.uid === bookData.ownerId);
       }
    }, [user, bookData]);
  
  useEffect(() => {
    loadSavedItems();
  }, [bookId, category]);

  const loadSavedItems = async () => {
    try {
      const items = await websearchService.getItems(bookId, { 
        type: category,
        saved: true 
      });
      setSavedItems(items);
    } catch (error) {
      console.error('Error loading saved items:', error);
    }
  };

  useEffect(() => {
    loadSearchPrompts();
  }, [bookId]);

  const loadSearchPrompts = async () => {
    try {
      const prompts = await websearchService.getSearchPrompts(bookId);
      setSearchPrompts(prompts);
      if (prompts.length > 0 && !selectedPrompt) {
        setSelectedPrompt(prompts[0]);
      }
    } catch (error) {
      console.error('Error loading prompts:', error);
    }
  };

  // Handle prompt selection - populates input with editable prompt
  const handlePromptSelect = (prompt) => {
    setSelectedPrompt(prompt);
    // Put the prompt in the search input so user can edit it
    setSearchQuery(prompt.prompt.replace('{query}', ''));
    setShowPromptEditor(false);
  };

  const handleSearch = async (formData) => {
    if (!formData.query.trim()) return;

    setIsSearching(true);
    setError(null);
    setSearchQuery(formData.query);

    try {
      const searchLeads = httpsCallable(functions, 'searchLeads');
      const response = await searchLeads({
        query: formData.query,
        category: formData.category,
        location: formData.location,
        targetUrls: groundingUrls,
        bookId
      });

      const newResults = response.data.results || [];
      
      const uniqueResults = newResults.filter(newItem => 
        !savedItems.some(savedItem => isSameItem(newItem, savedItem))
      );

      setItems(uniqueResults);
      setViewModeState('search');
      
      if (uniqueResults.length < newResults.length) {
        const duplicateCount = newResults.length - uniqueResults.length;
        setError(`Found ${newResults.length} results (${duplicateCount} already saved)`);
      }
    } catch (error) {
      if (error.message.includes('No API key available')) {
        setShowApiKeyPrompt(true);
        setError('Please add your Brave API key to use search');
      } else {
        setError(error.message);
      }
    } finally {
      setIsSearching(false);
    }
  };

  console.log('Search results', items);

  
  const handleToggleSave = async (item) => {
    const isSaved = savedItems.some(saved => saved.id === item.id);

    try {
      if (isSaved) {
        await websearchService.deleteItem(bookId, item.id);
        setSavedItems(prev => prev.filter(saved => saved.id !== item.id));
      } else {
        await websearchService.saveItem(bookId, user.uid, {
          ...item,
          saved: true,
          type: category
        });
        setSavedItems(prev => [...prev, { ...item, saved: true }]);
      }
    } catch (error) {
      console.error('Error toggling save:', error);
      setError('Failed to save item');
    }
  };

  const handleUpdateItem = async (updatedItem) => {
    try {
      await websearchService.updateItem(bookId, updatedItem.id, updatedItem);
      
      setSavedItems(prev => 
        prev.map(item => item.id === updatedItem.id ? updatedItem : item)
      );
      
      setItems(prev => 
        prev.map(item => item.id === updatedItem.id ? updatedItem : item)
      );
    } catch (error) {
      console.error('Error updating item:', error);
      setError('Failed to update item');
    }
  };

  const handleOpenSidebar = useCallback((item) => {
    setSidebarItem(item);
    setIsSidebarOpen(true);
  }, []);

  const handleExport = () => {
    const dataToExport = viewModeState === 'search' ? items : savedItems;
    const csv = convertToCSV(dataToExport);
    downloadCSV(csv, `${category}_export_${Date.now()}.csv`);
  };

  const convertToCSV = (data) => {
    if (!data.length) return '';
    
    const headers = Object.keys(data[0]).filter(key => 
      !['id', 'type', 'saved', 'createdAt', 'updatedAt'].includes(key)
    );
    
    const headerRow = headers.join(',');
    const rows = data.map(item => 
      headers.map(header => {
        const val = item[header];
        if (typeof val === 'string' && val.includes(',')) {
          return `"${val.replace(/"/g, '""')}"`;
        }
        return val || '';
      }).join(',')
    );
    
    return [headerRow, ...rows].join('\n');
  };

  const handleSaveFromModal = async (leadData) => {
    try {
      if (editingItem) {
        // Update existing lead
        await websearchService.updateItem(bookId, leadData.id, leadData);
        setSavedItems(prev => prev.map(item => item.id === leadData.id ? leadData : item));
      } else {
        // Add new lead
        await websearchService.saveItem(bookId, user.uid, leadData);
        setSavedItems(prev => [...prev, leadData]);
      }
      setSuccess('Lead saved successfully!');
      setTimeout(() => setSuccess(null), 3000);
    } catch (error) {
      console.error('Error saving lead:', error);
      setError('Failed to save lead');
    }
  };

  const downloadCSV = (csv, filename) => {
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleUrlChange = (index, value) => {
    const newUrls = [...targetUrls];
    newUrls[index] = value;
    setTargetUrls(newUrls);
  };

  const addUrlField = () => {
    if (targetUrls.length < 3) {
      setTargetUrls([...targetUrls, '']);
    }
  };

  const removeUrlField = (index) => {
    if (targetUrls.length > 1) {
      setTargetUrls(targetUrls.filter((_, i) => i !== index));
    }
  };

  const getPlaceholder = () => {
    if (category === 'lead') {
      return "Search leads (e.g. 'Startup Founders' or 'Marketing Directors')";
    } else if (category === 'supplier') {
      return "Search suppliers (e.g. 'Wholesale fabric' or 'Packaging manufacturers')";
    } else {
      return "Search tenders (e.g. 'Government security contracts')";
    }
  };

  const savedItemIds = new Set(savedItems.map(item => item.id));
  const displayedSavedItems = savedItems;
  const currentList = viewModeState === 'search' ? items : displayedSavedItems;
  const showGrounding = viewModeState === 'search' && !isSearching && groundingUrls.length > 0;

  // Theme classes
  const bgClass = isDarkMode ? 'bg-slate-900' : 'bg-gray-50';
  const textClass = isDarkMode ? 'text-slate-100' : 'text-gray-900';
  const cardBgClass = isDarkMode ? 'bg-slate-800' : 'bg-white';
  const borderClass = isDarkMode ? 'border-slate-700' : 'border-gray-200';
  const headerBgClass = isDarkMode ? 'bg-slate-950/50' : 'bg-white';

  if (viewMode === 'management' && hasManagementAccess) {
      return (
        <>
          <WebSearchManager
            user={user}
            bookId={bookId}
            bookData={bookData}
            onUpdate={loadSavedItems}
            customStyling={customStyling}
          />
          
        </>
      );
    }  

  return (
    <div className={`min-h-screen ${bgClass} ${textClass} flex flex-col`}>
      {/* Header - Replace lines 328-403 */}
      <header className={`${headerBgClass} border-b ${borderClass} sticky top-0 z-30 backdrop-blur-lg bg-opacity-90`}>
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {bookData?.logoUrl && (
                <img 
                  src={bookData.logoUrl} 
                  alt={bookData.botName}
                  className="h-10 w-10 object-contain"
                />
              )}
              <div>
                <h1 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  {bookData?.botName || 'Lead Scout'}
                </h1>
              </div>
            </div>

            <div className="flex items-center gap-2 md:gap-3">
              {/* Dark Mode Toggle */}
              <button
                onClick={() => setIsDarkMode(!isDarkMode)}
                className={`p-2 rounded-lg transition-all ${
                  isDarkMode 
                    ? 'bg-slate-800 text-yellow-400 hover:bg-slate-700' 
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
                title={isDarkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
              >
                {isDarkMode ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
              </button>

              {/* Export Button */}
              {(items.length > 0 || savedItems.length > 0) && (
                <button
                  onClick={handleExport}
                  className={`p-2 md:px-4 md:py-2 rounded-lg font-medium transition-colors flex items-center gap-2 ${
                    isDarkMode
                      ? 'bg-slate-800 text-slate-200 hover:bg-slate-700'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                  title="Export"
                >
                  <Download className="h-4 w-4 md:h-5 md:w-5" />
                  <span className="hidden md:inline">Export</span>
                </button>
              )}

              {/* Add Item Button */}
              <button
                onClick={() => {
                  setEditingItem(null);
                  setIsAddLeadModalOpen(true);
                }}
                style={{ backgroundColor: customStyling.primaryColor }}
                className="p-2 md:px-4 md:py-2 text-white rounded-lg font-medium hover:opacity-90 transition-opacity flex items-center gap-2"
                title="Add Item"
              >
                <Plus className="h-4 w-4 md:h-5 md:w-5" />
                <span className="hidden md:inline">Add Item</span>
              </button>

              {/* Desktop: User Profile */}
              <div className="hidden md:block">
                <UserProfile
                  user={user}
                  styling={customStyling}
                  bookData={bookData}
                  isOwner={isOwner}
                  actions={{
                    onProfile: () => setShowProfileModal(true),
                  }}
                  logout={logout}
                  setShowAuthModal={setShowAuthModal}
                  setShowProfileModal={setShowProfileModal}
                  shouldShowUserProfile={() => true}
                />
              </div>

              {/* Mobile: Menu Button */}
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className={`md:hidden p-2 rounded-lg transition-all ${
                  isDarkMode 
                    ? 'bg-slate-800 text-white hover:bg-slate-700' 
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
                title="Menu"
              >
                {user ? (
                  <div className="relative">
                    {user?.photoURL ? (
                      <img 
                        src={user.photoURL} 
                        alt="Profile" 
                        className="h-8 w-8 rounded-full object-cover"
                      />
                    ) : (
                      <div 
                        className="h-8 w-8 rounded-full flex items-center justify-center text-white text-sm font-medium"
                        style={{ backgroundColor: customStyling.primaryColor }}
                      >
                        {(user?.displayName?.[0] || user?.email?.[0] || 'U').toUpperCase()}
                      </div>
                    )}
                    {mobileMenuOpen && (
                      <div className="absolute top-0 right-0 w-2 h-2 bg-green-500 rounded-full border-2 border-white" />
                    )}
                  </div>
                ) : (
                  <User className="h-5 w-5" />
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu Dropdown */}
        {mobileMenuOpen && (
          <div className={`md:hidden border-t ${borderClass}`}>
            <div className={`container mx-auto px-4 py-4 ${isDarkMode ? 'bg-slate-900' : 'bg-white'}`}>
              <UserProfile
                user={user}
                styling={customStyling}
                bookData={bookData}
                isOwner={isOwner}
                isDarkMode={isDarkMode}
                logout={logout}
                setShowAuthModal={setShowAuthModal}
                setShowProfileModal={setShowProfileModal}
                setMobileMenuOpen={setMobileMenuOpen}
                shouldShowUserProfile={() => true}
              />
            </div>
          </div>
        )}
      </header>

      {/* Main Content */}
      <main className="flex-grow container mx-auto px-4 py-8 max-w-4xl">
        <div className="mb-8 space-y-6">
          {/* Hero Section */}
          <div className="text-center max-w-2xl mx-auto mb-10">
            <h2 className={`text-3xl md:text-4xl font-bold mb-4 ${
              isDarkMode 
                ? 'bg-clip-text text-transparent bg-gradient-to-r from-indigo-400 via-purple-400 to-indigo-400'
                : 'text-gradient-to-r from-indigo-400 via-purple-400 to-indigo-400'
            }`}>
              {headerTitle}
            </h2>
            <p className={`text-lg ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>
              {headerSubtitle}
            </p>
          </div>

          {/* Category Tabs */}
          {enabledCategories.length > 1 && (
            <div className="flex justify-center mb-6">
              <div className={`inline-flex rounded-xl p-1 ${
                isDarkMode ? 'bg-slate-900/50' : 'bg-gray-100'
              } border ${isDarkMode ? 'border-slate-800' : 'border-gray-200'}`}>
                {enabledCategories.map((cat) => (
                  <button
                    key={cat}
                    type="button"
                    onClick={() => setCategory(cat)}
                    className={`px-6 py-2.5 rounded-lg font-medium transition-all ${
                      category === cat
                        ? isDarkMode
                          ? 'bg-slate-700 text-white shadow-lg'
                          : 'bg-white text-gray-900 shadow-md'
                        : isDarkMode
                          ? 'text-slate-400 hover:text-slate-200'
                          : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    {cat === 'lead' && <Users className="inline h-4 w-4 mr-2" />}
                    {cat === 'supplier' && <Package className="inline h-4 w-4 mr-2" />}
                    {cat === 'tender' && <FileText className="inline h-4 w-4 mr-2" />}
                    {cat.charAt(0).toUpperCase() + cat.slice(1)}s
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Search Form */}
          <form onSubmit={(e) => {
            e.preventDefault();
            const filteredUrls = targetUrls.filter(url => url.trim());
            handleSearch({
              query: searchQuery,
              category,
              location: locationFilter,
              targetUrls: filteredUrls.length > 0 ? filteredUrls : undefined
            });
          }} className="space-y-3">
            
            {/* Location Bar */}
            <div className={`grid grid-cols-1 md:grid-cols-12 gap-2 p-2 rounded-xl border shadow-sm ${
              isDarkMode 
                ? 'bg-slate-900/50 border-slate-700/50' 
                : 'bg-white border-slate-200'
            }`}>
              <div className="md:col-span-4 relative">
                <label className={`absolute text-[10px] top-1 left-3 uppercase font-bold tracking-wider ${
                  isDarkMode ? 'text-slate-500' : 'text-slate-400'
                }`}>
                  Country
                </label>
                <select 
                  value={locationFilter.country}
                  onChange={(e) => setLocationFilter(prev => ({ ...prev, country: e.target.value }))}
                  className={`w-full border rounded-lg pt-5 pb-2 px-3 text-sm focus:border-indigo-500 focus:outline-none appearance-none cursor-pointer hover:bg-opacity-80 transition-colors ${
                    isDarkMode
                      ? 'bg-slate-800 border-slate-700 text-white'
                      : 'bg-slate-50 border-slate-200 text-slate-900'
                  }`}
                >
                  <option value="">Global / Any</option>
                  {COUNTRIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
                <div className={`absolute right-3 top-1/2 translate-y-1 pointer-events-none ${
                  isDarkMode ? 'text-slate-500' : 'text-slate-400'
                }`}>
                  <ChevronDown className="w-4 h-4" />
                </div>
              </div>
              
              <div className="md:col-span-4 relative">
                <label className={`absolute text-[10px] top-1 left-3 uppercase font-bold tracking-wider ${
                  isDarkMode ? 'text-slate-500' : 'text-slate-400'
                }`}>
                  State / Province
                </label>
                <input 
                  type="text" 
                  value={locationFilter.region}
                  onChange={(e) => setLocationFilter(prev => ({ ...prev, region: e.target.value }))}
                  placeholder="Optional"
                  className={`w-full border rounded-lg pt-5 pb-2 px-3 text-sm focus:border-indigo-500 focus:outline-none ${
                    isDarkMode
                      ? 'bg-slate-800 border-slate-700 text-white placeholder-slate-600'
                      : 'bg-slate-50 border-slate-200 text-slate-900 placeholder-slate-400'
                  }`}
                />
              </div>
              
              <div className="md:col-span-4 relative">
                <label className={`absolute text-[10px] top-1 left-3 uppercase font-bold tracking-wider ${
                  isDarkMode ? 'text-slate-500' : 'text-slate-400'
                }`}>
                  City
                </label>
                <input 
                  type="text" 
                  value={locationFilter.city}
                  onChange={(e) => setLocationFilter(prev => ({ ...prev, city: e.target.value }))}
                  placeholder="Optional"
                  className={`w-full border rounded-lg pt-5 pb-2 px-3 text-sm focus:border-indigo-500 focus:outline-none ${
                    isDarkMode
                      ? 'bg-slate-800 border-slate-700 text-white placeholder-slate-600'
                      : 'bg-slate-50 border-slate-200 text-slate-900 placeholder-slate-400'
                  }`}
                />
              </div>
            </div>

            {searchPrompts.length > 0 ? (
            
                <div className={`p-4 rounded-lg border space-y-3 ${
                  isDarkMode ? 'bg-slate-900 border-slate-700' : 'bg-white border-gray-200'
                }`}>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                    {searchPrompts.map(prompt => (
                      <button
                        key={prompt.id}
                        onClick={() => handlePromptSelect(prompt)}
                        className={`p-3 rounded-lg border text-left ${
                          selectedPrompt?.id === prompt.id
                            ? isDarkMode ? 'bg-indigo-900/30 border-indigo-600' : 'bg-indigo-50 border-indigo-500'
                            : isDarkMode ? 'bg-slate-800 border-slate-700 hover:border-indigo-500' : 'bg-gray-50 border-gray-200 hover:border-indigo-400'
                        }`}
                      >
                        <FileText className="h-4 w-4 inline mr-2" />
                        <span className="font-medium text-sm">{prompt.categoryName}</span>
                      </button>
                    ))}
                  </div>
                </div>
            
          ) : (
            <div className={`p-4 rounded-lg border text-center ${
              isDarkMode ? 'bg-slate-800/50 border-slate-700' : 'bg-gray-50 border-gray-200'
            }`}>
              <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>
                No search categories yet.
              </p>
            </div>
          )}

            {/* Editable Prompt/Query Input */}
            <div className="relative group">
              <div className="absolute -inset-1 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-xl opacity-25 group-hover:opacity-50 blur transition duration-200"></div>
              <div className={`relative flex items-start rounded-xl border shadow-xl overflow-hidden ${
                isDarkMode 
                  ? 'bg-slate-900 border-slate-700 shadow-2xl' 
                  : 'bg-white border-slate-200'
              }`}>
                <div className={`pl-4 pt-4 ${isDarkMode ? 'text-slate-400' : 'text-slate-400'}`}>
                  <Search className="w-6 h-6" />
                </div>
                <textarea
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder={selectedPrompt 
                    ? "Edit the prompt or enter your specific search query..."
                    : "Select a category first, then enter your search..."
                  }
                  rows={3}
                  className={`w-full bg-transparent border-none focus:ring-0 py-4 px-4 text-base focus:outline-none resize-none ${
                    isDarkMode
                      ? 'text-white placeholder-slate-500'
                      : 'text-slate-900 placeholder-slate-400'
                  }`}
                  disabled={isSearching || !selectedPrompt}
                />
                <div className="pr-2 pt-2">
                  <button
                    type="submit"
                    disabled={isSearching || !searchQuery.trim() || !selectedPrompt}
                    className={`px-6 py-2 rounded-lg font-medium transition-all duration-200 flex items-center gap-2 ${
                      isSearching || !searchQuery.trim() || !selectedPrompt
                        ? isDarkMode
                          ? 'bg-slate-800 text-slate-500 cursor-not-allowed'
                          : 'bg-slate-100 text-slate-400 cursor-not-allowed'
                        : 'bg-indigo-600 hover:bg-indigo-500 text-white shadow-lg shadow-indigo-500/30'
                    }`}
                  >
                    {isSearching ? (
                      <>
                        <Loader2 className="animate-spin h-5 w-5" />
                        <span>Searching...</span>
                      </>
                    ) : (
                      <span>Search</span>
                    )}
                  </button>
                </div>
              </div>
            </div>
            {/* Target URL Toggle & Input */}
            <div className="flex flex-col items-center gap-2">
              {!showTargetUrl && (
                <button 
                  type="button"
                  onClick={() => setShowTargetUrl(true)}
                  className={`mt-2 flex items-center gap-2 px-4 py-2 border rounded-lg text-sm transition-all ${
                    isDarkMode
                      ? 'bg-slate-800/50 hover:bg-slate-800 border-slate-700 text-slate-400 hover:text-indigo-400'
                      : 'bg-white hover:bg-slate-50 border-slate-200 text-slate-500 hover:text-indigo-500'
                  }`}
                >
                  <LinkIcon className="w-4 h-4" />
                  {category === 'tender' ? 'Configure Target Tender Portals' : 'Limit search to specific websites'}
                </button>
              )}
              
              {showTargetUrl && (
                <div className={`w-full max-w-xl space-y-2 p-4 rounded-xl border mt-2 shadow-sm ${
                  isDarkMode
                    ? 'bg-slate-800/30 border-slate-700/50'
                    : 'bg-white border-slate-200'
                }`}>
                  <div className="flex justify-between items-center px-1 mb-1">
                    <span className={`text-xs font-semibold uppercase tracking-wider ${
                      isDarkMode ? 'text-slate-300' : 'text-slate-500'
                    }`}>
                      {category === 'tender' ? 'Target Portals / Websites' : 'Target Websites'}
                    </span>
                    <button 
                      type="button"
                      onClick={() => { 
                        setShowTargetUrl(false); 
                        setTargetUrls(['']); 
                      }}
                      className={`text-xs flex items-center gap-1 ${
                        isDarkMode
                          ? 'text-slate-500 hover:text-red-400'
                          : 'text-slate-400 hover:text-red-500'
                      }`}
                    >
                      <X className="w-3 h-3" />
                      Close & Clear
                    </button>
                  </div>
                  
                  {targetUrls.map((url, index) => (
                    <div key={index} className="relative flex items-center group/input">
                      <div className={`absolute left-3 transition-colors ${
                        isDarkMode ? 'text-slate-500' : 'text-slate-400'
                      }`}>
                        <Globe className="w-4 h-4" />
                      </div>
                      <input
                        type="text"
                        value={url}
                        onChange={(e) => handleUrlChange(index, e.target.value)}
                        placeholder={category === 'tender' ? "e.g. www.etenders.gov.za" : "e.g. linkedin.com"}
                        className={`w-full border rounded-lg py-2.5 pl-9 pr-8 text-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 transition-colors ${
                          isDarkMode
                            ? 'bg-slate-900 border-slate-700 text-white placeholder-slate-600'
                            : 'bg-slate-50 border-slate-200 text-slate-900 placeholder-slate-400'
                        }`}
                      />
                      {targetUrls.length > 1 && (
                        <button 
                          type="button"
                          onClick={() => removeUrlField(index)}
                          className={`absolute right-2 p-1 ${
                            isDarkMode
                              ? 'text-slate-600 hover:text-red-400'
                              : 'text-slate-400 hover:text-red-500'
                          }`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  ))}
                  
                  {targetUrls.length < 3 && (
                    <button 
                      type="button"
                      onClick={addUrlField}
                      className={`text-xs flex items-center gap-1 ml-1 font-medium ${
                        isDarkMode
                          ? 'text-indigo-400 hover:text-indigo-300'
                          : 'text-indigo-600 hover:text-indigo-500'
                      }`}
                    >
                      <Plus className="w-3 h-3" />
                      Add another website
                    </button>
                  )}
                  
                  <p className={`text-[10px] text-center mt-2 border-t pt-2 ${
                    isDarkMode
                      ? 'text-slate-500 border-slate-700/50'
                      : 'text-slate-500 border-slate-200'
                  }`}>
                    Enter specific domains (e.g., <code>gov.za</code> or <code>company.com</code>) to restrict your search results.
                  </p>
                </div>
              )}
            </div>

            {/* Example Queries */}
            <div className={`mt-3 flex justify-center gap-4 text-xs ${
              isDarkMode ? 'text-slate-500' : 'text-slate-500'
            }`}>
              {category === 'lead' && (
                <>
                  <span>Try: "CFOs of Tech companies"</span>
                  <span>•</span>
                  <span>"Yoga studios"</span>
                </>
              )}
              {category === 'supplier' && (
                <>
                  <span>Try: "Wholesale fabric"</span>
                  <span>•</span>
                  <span>"Packaging manufacturers"</span>
                </>
              )}
              {category === 'tender' && (
                <>
                  <span>Try: "Government security contracts"</span>
                  <span>•</span>
                  <span>"School feeding scheme"</span>
                </>
              )}
            </div>

            {/* Error/Success Messages */}
            {error && (
              <div className={`p-4 rounded-lg flex items-center gap-3 ${
                isDarkMode
                  ? 'bg-red-500/10 border border-red-500/50 text-red-400'
                  : 'bg-red-50 border border-red-200 text-red-700'
              }`}>
                <AlertCircle className="h-5 w-5 flex-shrink-0" />
                <span>{error}</span>
              </div>
            )}

            {success && (
              <div className={`p-4 rounded-lg flex items-center gap-3 ${
                isDarkMode
                  ? 'bg-green-500/10 border border-green-500/50 text-green-400'
                  : 'bg-green-50 border border-green-200 text-green-700'
              }`}>
                <Check className="h-5 w-5 flex-shrink-0" />
                <span>{success}</span>
              </div>
            )}
          </form>
        </div>

        {/* View Toggle Tabs */}
        {(items.length > 0 || savedItems.length > 0 || isSearching) && (
          <div className="flex justify-center mb-8">
            <div className={`${isDarkMode ? 'bg-slate-800/50' : 'bg-gray-100'} p-1 rounded-xl flex gap-1 border ${borderClass}`}>
              <button
                onClick={() => setViewModeState('search')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  viewModeState === 'search' 
                    ? isDarkMode
                      ? 'bg-slate-700 text-white shadow-sm' 
                      : 'bg-white text-gray-900 shadow-md'
                    : isDarkMode
                      ? 'text-slate-400 hover:text-slate-200 hover:bg-slate-800'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                }`}
              >
                Search Results {items.length > 0 && `(${items.length})`}
              </button>
              <button
                onClick={() => setViewModeState('saved')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${
                  viewModeState === 'saved' 
                    ? isDarkMode
                      ? 'bg-slate-700 text-white shadow-sm' 
                      : 'bg-white text-gray-900 shadow-md'
                    : isDarkMode
                      ? 'text-slate-400 hover:text-slate-200 hover:bg-slate-800'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                }`}
              >
                <Star className="h-4 w-4" />
                Saved {savedItems.length > 0 && `(${savedItems.length})`}
              </button>
            </div>
          </div>
        )}

        {/* Results Table */}
        <ResultsTable
          items={currentList}
          isLoading={isSearching}
          savedItemIds={savedItemIds}
          onToggleSave={handleToggleSave}
          onUpdateItem={handleUpdateItem}
          onEdit={(item) => {
            setEditingItem(item);
            setIsAddLeadModalOpen(true);
          }}
          onOpenSidebar={handleOpenSidebar}
          isDarkMode={isDarkMode}
          primaryColor={customStyling.primaryColor}
        />

        {/* Empty State */}
        {viewModeState === 'saved' && currentList.length === 0 && !isSearching && (
          <div className={`text-center py-12 ${cardBgClass} border ${borderClass} rounded-xl`}>
            <div className={`inline-flex items-center justify-center w-16 h-16 rounded-full mb-4 ${
              isDarkMode ? 'bg-slate-800' : 'bg-gray-100'
            }`}>
              <Star className={`w-8 h-8 ${isDarkMode ? 'text-slate-500' : 'text-gray-400'}`} />
            </div>
            <h3 className={`text-lg font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              No saved {category}s yet
            </h3>
            <p className={`mt-2 max-w-sm mx-auto ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>
              Search and save items to build your list.
            </p>
            <div className="mt-6 flex justify-center items-center gap-4">
              <button 
                onClick={() => setViewModeState('search')}
                style={{ color: customStyling.primaryColor }}
                className="font-medium text-sm hover:underline"
              >
                Go back to search
              </button>
              {category === 'lead' && (
                <>
                  <span className={isDarkMode ? 'text-slate-600' : 'text-gray-400'}>|</span>
                  <button 
                    onClick={() => { 
                      setEditingItem(null); 
                      setIsAddLeadModalOpen(true); 
                    }}
                    style={{ color: customStyling.primaryColor }}
                    className="font-medium text-sm hover:underline"
                  >
                    Add manually
                  </button>
                </>
              )}
            </div>
          </div>
        )}
      </main>
      
      {showApiKeyPrompt && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 max-w-md w-full">
            <div className="flex items-center gap-3 mb-4">
              <Sparkles className="h-6 w-6 text-purple-600" />
              <h3 className="text-lg font-semibold">API Key Required</h3>
            </div>
            <p className="text-sm text-gray-600 mb-4">
              To use AI-powered search, please add your Gemini API key in your profile settings.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowProfileModal(true);
                  setShowApiKeyPrompt(false);
                }}
                className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
              >
                Go to Profile
              </button>
              <button
                onClick={() => setShowApiKeyPrompt(false)}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Engagement Sidebar */}
      {isSidebarOpen && sidebarItem && (
        <EngagementSidebar
          item={sidebarItem}
          bookId={bookId}
          onClose={() => {
            setIsSidebarOpen(false);
            setSidebarItem(null);
          }}
          onUpdate={handleUpdateItem}
          isDarkMode={isDarkMode}
        />
      )}

      {/* Add Lead Modal */}
      {isAddLeadModalOpen && (
        <AddLeadModal
          isOpen={isAddLeadModalOpen}
          onClose={() => {
            setIsAddLeadModalOpen(false);
            setEditingItem(null);
          }}
          onSave={handleSaveFromModal}
          editingLead={editingItem}
          isDarkMode={isDarkMode}
        />
      )}

      <AuthModal 
        isOpen={showAuthModal} 
        onClose={() => setShowAuthModal(false)}
        defaultView="signin" // or "signup"
      />

      {showProfileModal && (
        <ProfileModal
          onClose={() => setShowProfileModal(false)}
          onSave={(updatedProfile) => {
            console.log('Profile updated:', updatedProfile);
            setShowProfileModal(false);
          }}
        />
      )}

      {/* Share Modal */}
      <ShareModal
        content={bookData}
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
        trackingEnabled={enableShareTracking}
      />
    </div>
  );
};

// ResultsTable Component
const ResultsTable = ({ 
  items, 
  isLoading, 
  savedItemIds, 
  onToggleSave, 
  onUpdateItem, 
  onEdit, 
  onOpenSidebar,
  isDarkMode,
  primaryColor 
}) => {
  const [processingItems, setProcessingItems] = useState(new Set());

  const handleSaveAction = (item) => {
    if (processingItems.has(item.id)) return;
    
    setProcessingItems(prev => new Set(prev).add(item.id));
    
    setTimeout(() => {
      onToggleSave(item);
      setProcessingItems(prev => {
        const next = new Set(prev);
        next.delete(item.id);
        return next;
      });
    }, 600);
  };

  const getEmailLink = (email, subjectPrefix) => {
    if (!email) return '#';
    return `mailto:${email}?subject=${encodeURIComponent(subjectPrefix)}`;
  };

  const getWhatsAppLink = (phone, name) => {
    if (!phone) return '#';
    const cleanPhone = phone.replace(/[^0-9]/g, '');
    const text = `Hi ${name}, I found your contact info and wanted to reach out.`;
    return `https://wa.me/${cleanPhone}?text=${encodeURIComponent(text)}`;
  };

  const bgClass = isDarkMode ? 'bg-slate-900' : 'bg-white';
  const borderClass = isDarkMode ? 'border-slate-800' : 'border-gray-200';
  const headerBgClass = isDarkMode ? 'bg-slate-950/50' : 'bg-gray-50';
  const textClass = isDarkMode ? 'text-slate-400' : 'text-gray-600';
  const hoverClass = isDarkMode ? 'hover:bg-slate-800/30' : 'hover:bg-gray-50';

  if (isLoading) {
    return (
      <div className={`w-full ${bgClass} border ${borderClass} rounded-xl overflow-hidden shadow-xl`}>
        <div className="animate-pulse">
          <div className={`h-12 ${headerBgClass} w-full border-b ${borderClass}`}></div>
          {[...Array(5)].map((_, i) => (
            <div key={i} className={`flex items-center p-4 border-b ${borderClass}`}>
              <div className={`h-10 w-10 ${isDarkMode ? 'bg-slate-800' : 'bg-gray-200'} rounded-full mr-4`}></div>
              <div className="flex-1 space-y-2">
                <div className={`h-4 ${isDarkMode ? 'bg-slate-800' : 'bg-gray-200'} rounded w-1/4`}></div>
                <div className={`h-3 ${isDarkMode ? 'bg-slate-800' : 'bg-gray-200'} rounded w-1/3`}></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (items.length === 0) return null;

  const itemType = items[0].type;

  return (
    <div className={`w-full overflow-x-auto ${bgClass} border ${borderClass} rounded-xl shadow-xl`}>
      <table className="w-full text-left border-collapse">
        <thead>
          <tr className={`${headerBgClass} ${textClass} text-xs uppercase tracking-wider border-b ${borderClass}`}>
            {itemType === 'lead' && (
              <>
                <th className="p-4 font-medium w-1/4">Name & Role</th>
                <th className="p-4 font-medium w-1/4">Company & Location</th>
                <th className="p-4 font-medium w-1/5">Connect</th>
              </>
            )}
            {itemType === 'supplier' && (
              <>
                <th className="p-4 font-medium w-1/4">Company</th>
                <th className="p-4 font-medium w-1/4">Description</th>
                <th className="p-4 font-medium w-1/5">Contact</th>
              </>
            )}
            {itemType === 'tender' && (
              <>
                <th className="p-4 font-medium w-1/3">Title & Organization</th>
                <th className="p-4 font-medium w-1/5">Details</th>
                <th className="p-4 font-medium w-1/6">Status</th>
                <th className="p-4 font-medium w-1/6">Link</th>
              </>
            )}
            <th className="p-4 font-medium text-right">Actions</th>
          </tr>
        </thead>
        <tbody className={`divide-y ${borderClass}`}>
          {items.map((item) => {
            const isSaved = savedItemIds.has(item.id);
            const isProcessing = processingItems.has(item.id);

            return (
              <tr key={item.id} className={`${hoverClass} transition-colors group`}>
                {/* LEAD ROWS */}
                {item.type === 'lead' && (
                  <>
                    <td className="p-4 align-top">
                      <div className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                        {item.name}
                      </div>
                      {item.role && (
                        <div className={`text-sm ${isDarkMode ? 'text-indigo-400' : 'text-indigo-600'}`}>
                          {item.role}
                        </div>
                      )}
                    </td>
                    <td className="p-4 align-top">
                      <div className={`${isDarkMode ? 'text-slate-200' : 'text-gray-700'}`}>
                        {item.company}
                      </div>
                      {item.location && (
                        <div className={`text-xs ${textClass} mt-1`}>{item.location}</div>
                      )}
                    </td>
                    <td className="p-4 align-top">
                      <div className="flex flex-col gap-2">
                        {item.email && (
                          <a
                            href={getEmailLink(item.email, `Hello ${item.name || item.company}`)}
                            className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors inline-flex items-center gap-1.5 ${
                              isDarkMode
                                ? 'bg-indigo-600/20 text-indigo-300 border border-indigo-500/30 hover:bg-indigo-600 hover:text-white'
                                : 'bg-indigo-50 text-indigo-700 border border-indigo-200 hover:bg-indigo-600 hover:text-white'
                            }`}
                          >
                            <Mail className="h-3 w-3" />
                            Email
                          </a>
                        )}
                        {item.phone && (
                          <a
                            href={`tel:${item.phone}`}
                            className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors inline-flex items-center gap-1.5 ${
                              isDarkMode
                                ? 'bg-green-600/20 text-green-300 border border-green-500/30 hover:bg-green-600 hover:text-white'
                                : 'bg-green-50 text-green-700 border border-green-200 hover:bg-green-600 hover:text-white'
                            }`}
                          >
                            <Phone className="h-3 w-3" />
                            Call
                          </a>
                        )}
                        {item.website && (
                          <a
                            href={item.website}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors inline-flex items-center gap-1.5 ${
                              isDarkMode
                                ? 'bg-slate-700/50 text-slate-300 border border-slate-600 hover:bg-slate-700'
                                : 'bg-gray-100 text-gray-700 border border-gray-300 hover:bg-gray-200'
                            }`}
                          >
                            <Globe className="h-3 w-3" />
                            Website
                          </a>
                        )}
                        {item.linkedin && (
                          <a
                            href={item.linkedin}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors inline-flex items-center gap-1.5 ${
                              isDarkMode
                                ? 'bg-blue-600/20 text-blue-300 border border-blue-500/30 hover:bg-blue-600 hover:text-white'
                                : 'bg-blue-50 text-blue-700 border border-blue-200 hover:bg-blue-600 hover:text-white'
                            }`}
                          >
                            <LinkIcon className="h-3 w-3" />
                            LinkedIn
                          </a>
                        )}
                      </div>
                    </td>
                  </>
                )}

                {/* SUPPLIER ROWS */}
                {item.type === 'supplier' && (
                  <>
                    <td className="p-4 align-top">
                      <div className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                        {item.name}
                      </div>
                      {item.location && (
                        <div className={`text-sm ${textClass} mt-1`}>{item.location}</div>
                      )}
                    </td>
                    <td className="p-4 align-top">
                      <p className={`text-sm ${isDarkMode ? 'text-slate-300' : 'text-gray-700'} line-clamp-2`}>
                        {item.description}
                      </p>
                    </td>
                    <td className="p-4 align-top">
                      <div className="flex flex-col gap-2">
                        {item.email && (
                          <a
                            href={getEmailLink(item.email, `Inquiry - ${item.name}`)}
                            className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors inline-flex items-center gap-1.5 ${
                              isDarkMode
                                ? 'bg-indigo-600/20 text-indigo-300 border border-indigo-500/30 hover:bg-indigo-600 hover:text-white'
                                : 'bg-indigo-50 text-indigo-700 border border-indigo-200 hover:bg-indigo-600 hover:text-white'
                            }`}
                          >
                            <Mail className="h-3 w-3" />
                            Contact
                          </a>
                        )}
                        {item.website && (
                          <a
                            href={item.website}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors inline-flex items-center gap-1.5 ${
                              isDarkMode
                                ? 'bg-slate-700/50 text-slate-300 border border-slate-600 hover:bg-slate-700'
                                : 'bg-gray-100 text-gray-700 border border-gray-300 hover:bg-gray-200'
                            }`}
                          >
                            <Globe className="h-3 w-3" />
                            Website
                          </a>
                        )}
                      </div>
                    </td>
                  </>
                )}

                {/* TENDER ROWS */}
                {item.type === 'tender' && (
                  <>
                    <td className="p-4 align-top">
                      <div className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                        {item.title}
                      </div>
                      <div className={`text-sm ${isDarkMode ? 'text-indigo-400' : 'text-indigo-600'}`}>
                        {item.organization}
                      </div>
                    </td>
                    <td className="p-4 align-top">
                      <div className={isDarkMode ? 'text-slate-200' : 'text-gray-700'}>
                        {item.location}
                      </div>
                      <div className={`text-xs ${textClass} mt-1`}>
                        Value: {item.value || 'N/A'}
                      </div>
                    </td>
                    <td className="p-4 align-top">
                      <div className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        isDarkMode
                          ? 'bg-emerald-900/30 text-emerald-400 border border-emerald-800'
                          : 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                      }`}>
                        {item.status}
                      </div>
                      <div className={`text-xs ${textClass} mt-2`}>
                        Deadline: {item.deadline}
                      </div>
                    </td>
                    <td className="p-4 align-top">
                      {item.link && (
                        <a 
                          href={item.link} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors inline-flex items-center gap-1.5 ${
                            isDarkMode
                              ? 'bg-indigo-600/20 text-indigo-300 border border-indigo-500/30 hover:bg-indigo-600 hover:text-white'
                              : 'bg-indigo-50 text-indigo-700 border border-indigo-200 hover:bg-indigo-600 hover:text-white'
                          }`}
                        >
                          <ExternalLink className="h-3 w-3" />
                          View
                        </a>
                      )}
                    </td>
                  </>
                )}

                {/* ACTIONS */}
                <td className="p-4 align-top text-right">
                  <div className="flex items-center justify-end gap-2">
                    <button
                      onClick={() => onOpenSidebar(item)}
                      className={`p-2 rounded-lg transition-colors ${
                        isDarkMode
                          ? 'text-slate-400 hover:text-white hover:bg-slate-700'
                          : 'text-gray-400 hover:text-gray-700 hover:bg-gray-100'
                      }`}
                      title="View engagement"
                    >
                      <MessageSquare className="h-4 w-4" />
                    </button>

                    {item.type === 'lead' && (
                      <button
                        onClick={() => onEdit(item)}
                        className={`p-2 rounded-lg transition-colors ${
                          isDarkMode
                            ? 'text-slate-400 hover:text-white hover:bg-slate-700'
                            : 'text-gray-400 hover:text-gray-700 hover:bg-gray-100'
                        }`}
                        title="Edit"
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                    )}

                    <button
                      onClick={() => handleSaveAction(item)}
                      disabled={isProcessing}
                      style={{ 
                        backgroundColor: isSaved ? undefined : primaryColor,
                        color: isSaved ? undefined : 'white'
                      }}
                      className={`p-2 rounded-lg transition-all ${
                        isSaved
                          ? isDarkMode
                            ? 'bg-slate-700 text-yellow-400'
                            : 'bg-gray-200 text-yellow-600'
                          : 'hover:opacity-90'
                      } ${isProcessing ? 'opacity-50 cursor-not-allowed' : ''}`}
                      title={isSaved ? 'Unsave' : 'Save'}
                    >
                      {isProcessing ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Star className={`h-4 w-4 ${isSaved ? 'fill-current' : ''}`} />
                      )}
                    </button>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};